import { Injectable } from '@angular/core';
import { CartActions, CartEntryApiService } from '@cart/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CartModification, normalizeHttpError, SiteContextActions, withdrawOn } from '@spartacus/core';
import { from, Observable } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

@Injectable()
export class CartEntryEffects {
  private contextChange$ = this.actions$.pipe(
    ofType(SiteContextActions.CURRENCY_CHANGE, SiteContextActions.LANGUAGE_CHANGE),
  );

  addEntry$: Observable<CartActions.CartAddEntrySuccess | CartActions.CartAddEntryFail | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartActions.CART_ADD_ENTRY),
        map((action: CartActions.CartAddEntry) => action.payload),
        concatMap((payload) => {
          return this.occCartEntryService
            .add(payload.cartId, payload.productCode, payload.quantity, payload.selectedPrice)
            .pipe(
              map(
                (cartModification: CartModification) =>
                  new CartActions.CartAddEntrySuccess({
                    ...payload,
                    ...(cartModification as Required<CartModification>),
                  }),
              ),
              catchError((error) =>
                from([
                  new CartActions.CartAddEntryFail({ ...payload, error: normalizeHttpError(error) }),
                  new CartActions.LoadCart({ cartId: payload.cartId, userId: payload.userId }),
                ]),
              ),
            );
        }),
        withdrawOn(this.contextChange$),
      ),
    );

  removeEntry$: Observable<
    CartActions.CartRemoveEntrySuccess | CartActions.CartRemoveEntryFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.CART_REMOVE_ENTRY),
      map((action: CartActions.CartRemoveEntry) => action.payload),
      concatMap((payload) =>
        this.occCartEntryService.remove(payload.cartId, payload.entryNumber).pipe(
          map(() => {
            return new CartActions.CartRemoveEntrySuccess({
              ...payload,
            });
          }),
          catchError((error) =>
            from([
              new CartActions.CartRemoveEntryFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                cartId: payload.cartId,
                userId: payload.userId,
              }),
            ]),
          ),
        ),
      ),
      withdrawOn(this.contextChange$),
    ),
  );

  updateEntry$: Observable<
    CartActions.CartUpdateEntrySuccess | CartActions.CartUpdateEntryFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.CART_UPDATE_ENTRY),
      map((action: CartActions.CartUpdateEntry) => action.payload),
      concatMap((payload) =>
        this.occCartEntryService.update(payload.cartId, payload.entryNumber, payload.quantity).pipe(
          map(() => {
            return new CartActions.CartUpdateEntrySuccess({
              ...payload,
            });
          }),
          catchError((error) =>
            from([
              new CartActions.CartUpdateEntryFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                cartId: payload.cartId,
                userId: payload.userId,
              }),
            ]),
          ),
        ),
      ),
      withdrawOn(this.contextChange$),
    ),
  );

  constructor(
    private actions$: Actions,
    private occCartEntryService: CartEntryApiService,
  ) {}
}
